import { useEffect, useState } from "react";
import apiClient from "../../../utils/apiClient";

const NO_CODING = { allowed: false, shown: false, runner: false, reason: "" };

/**
 * What the builder offers to pick from: quiz-bank categories, the batches and
 * courses an assessment can be given to (across every branch — an assessment
 * is institute-wide), and whether coding problems are available here.
 *
 * Coding availability is the SERVER's answer (GET /assessments/builder-options):
 * the plan, the institute's own switch for Coding Practice, and a code runner on
 * the server — the same rules the save is checked against. `shown` is the
 * module alone: when it is off the builder says nothing about coding at all.
 * It arrives on its own (`codingKnown`), because the first step waits for it.
 */
export default function useBuilderData({ isLibrary }) {
  const [state, setState] = useState({
    categories: [], batches: [], courses: [], loaded: false,
  });
  const [coding, setCoding] = useState({ coding: NO_CODING, codingKnown: false });

  useEffect(() => {
    let alive = true;
    apiClient.get("/assessments/builder-options")
      .then(({ data }) => {
        const c = data?.coding || {};
        return { ...NO_CODING, ...c, shown: c.enabled ?? !!c.allowed };
      })
      // Unknown is hidden: an institute with coding off must never see it.
      .catch(() => NO_CODING)
      .then((c) => { if (alive) setCoding({ coding: c, codingKnown: true }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const allBranches = { headers: { "X-Branch-Id": "all" } };
    const none = (key) => Promise.resolve({ data: { [key]: [] } });
    Promise.all([
      apiClient.get("/quiz-categories", { params: { flat: true } }).catch(() => ({ data: { categories: [] } })),
      isLibrary ? none("batches") : apiClient.get("/batches/list", { params: { pageSize: 500 }, ...allBranches })
        .catch(() => ({ data: { batches: [] } })),
      isLibrary ? none("courses") : apiClient.get("/courses/list", { params: { pageSize: 500 }, ...allBranches })
        .catch(() => ({ data: { courses: [] } })),
    ]).then(([cat, bat, crs]) => {
      if (!alive) return;
      setState({
        categories: cat.data.categories || [],
        batches: bat.data.batches || [],
        courses: crs.data.courses || [],
        loaded: true,
      });
    });
    return () => { alive = false; };
  }, [isLibrary]);

  return { ...state, ...coding };
}
